import {
  Form,
  Input,
  DatePicker,
  InputNumber,
  Divider,
  Select,
  Button,
  Row,
  Col,
} from "antd";
import { Rule } from "antd/lib/form";
import { PlusOutlined } from "@ant-design/icons";
import React, { Fragment, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import {
  creationFormItems,
  creationMap,
  creationPlaceholders,
  creationRoles,
  INPUT_MAX_LENGTH,
  requiredCreationField,
} from "../../utils-creation/constants";
import {
  getFuzzySearchResearchersID,
  resetState,
} from "features/content/setting_page/CreationPanel/creationPanelSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "utils/types";
import { getLabelForFuzzySearchResearcherID } from "../../utils-creation/utils";
import {
  FuzzySearchResearcherID,
  CreationField,
  CreationInputLength,
  CreationInputLengths,
} from "../../utils-creation/types";

const { Option } = Select;
const { TextArea } = Input;

const getRules = (field: CreationField): Rule[] | undefined => {
  const rules: Rule[] = [];
  if (requiredCreationField.includes(field))
    rules.push({
      required: true,
      message: "This field is required.",
    });

  if (field === "url")
    rules.push({
      type: "url",
      message: "Please enter a valid URL. The URL must start with 'https://' or 'http://'. If you are not sure, please copy the text in the browser's address bar and paste it here.",
    });

  if (Object.keys(INPUT_MAX_LENGTH).includes(field))
    rules.push({
      max: INPUT_MAX_LENGTH[field as CreationInputLength],
      message: "Character limit exceeded.",
    });

  return rules;
};

const getExtra = (field: CreationField, inputLengths: CreationInputLengths) => {
  return Object.keys(INPUT_MAX_LENGTH).includes(field) ? (
    <Extra
      inputLength={inputLengths[field as CreationInputLength]}
      maxLength={INPUT_MAX_LENGTH[field as CreationInputLength]}
    />
  ) : null;
};

const Extra = ({ message, inputLength, maxLength }: any) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontSize: "12px",
    }}
  >
    <span style={{ color: "initial" }}>{""}</span>
    <span
      style={{
        textAlign: "right",
        color: inputLength > maxLength ? "red" : "initial",
      }}
    >
      {inputLength || 0} / {maxLength} CHARACTERS
    </span>
  </div>
);

interface Props {
  inputLengths: CreationInputLengths;
  formType: "ADD" | "EDIT";
}

export default function CreationFormItems({ inputLengths, formType }: Props) {
  const dispatch = useDispatch();

  const { user } = useSelector((state: RootState) => state.authentication);

  const {
    fuzzySearchResearchersID: { results, status },
  } = useSelector((state: RootState) => state.creationPanel);

  const getFuzzySearchResearchersID_debounced = useMemo(
    () =>
      debounce((value) => {
        dispatch(getFuzzySearchResearchersID(value));
      }, 500),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  if (!user) return null;

  return (
    <>
      {creationFormItems.map((field) => {
        const creationFieldLabel = creationMap[field];
        if (creationFieldLabel === "Title")
          return (
            <Fragment key={field}>
              {formType === "ADD" && (
                <h1 style={{ textAlign: "center" }}>Add a new Research Creation</h1>
              )}
              {formType === "EDIT" && (
                <h1 style={{ textAlign: "center" }}>Edit a Research Creation</h1>
              )}
              <h3 style={{ textAlign: "center" }}>
                Required fields are denoted by{" "}
                <span style={{ color: "#ff4d4f" }}>*</span>.
              </h3>
              <br />
              <Form.Item
                label={creationFieldLabel}
                name={field}
                rules={getRules(field)}
                extra={getExtra(field, inputLengths)}
              >
                <TextArea rows={2} placeholder={creationPlaceholders[field]} />
              </Form.Item>
            </Fragment>
          );

        if (creationFieldLabel === "Premiere")
          return (
            <Fragment key={field}>
              <Divider style={{ width: "100%" }} />
              <h2 style={{ textAlign: "center" }}>PREMIERE</h2>
              <br />
              <Form.Item
                label={creationFieldLabel}
                name={field}
                rules={getRules(field)}
                extra={getExtra(field, inputLengths)}
              >
                <TextArea rows={2} placeholder={creationPlaceholders[field]} />
              </Form.Item>
            </Fragment>
          );

        if (
          creationFieldLabel === "Venue #1" ||
          creationFieldLabel === "Venue #2"
        )
          return (
            <Fragment key={field}>
              <Divider style={{ width: "100%" }} />
              {creationFieldLabel === "Venue #1" && (
                <>
                  <h2 style={{ textAlign: "center" }}>
                    SELECTED PRESENTATIONS
                  </h2>
                  <br />
                </>
              )}
              <Form.Item
                label={creationFieldLabel}
                name={field}
                rules={getRules(field)}
                extra={getExtra(field, inputLengths)}
              >
                <TextArea rows={2} placeholder={creationPlaceholders[field]} />
              </Form.Item>
            </Fragment>
          );

        if (creationFieldLabel === "Role(s)")
          return (
            <Fragment key={field}>
              <Divider style={{ width: "100%" }} />
              <Form.Item
                label={creationFieldLabel}
                name={field}
                rules={getRules(field)}
                extra={getExtra(field, inputLengths)}
              >
                <Select
                  mode="tags"
                  style={{ width: "100%" }}
                  placeholder={creationPlaceholders[field]}
                >
                  {creationRoles.map((role) => (
                    <Option
                      className="sub-heading-font"
                      key={role}
                      value={role}
                    >
                      {role}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Fragment>
          );

        if (creationFieldLabel === "Researchers to Add or Edit")
          return (
            <Fragment key={field}>
              <Divider style={{ width: "100%" }} />
              <Form.List
                name={field}
                initialValue={[
                  {
                    _researchers_id: {
                      label: getLabelForFuzzySearchResearcherID(
                        user as FuzzySearchResearcherID
                      ),
                      value: user.id,
                    },
                    _researchers_roles: [],
                  },
                ]}
              >
                {(fields, { add, remove }) => {
                  return (
                    <>
                      <h2 style={{ textAlign: "center" }}>
                        SFU FACULTY COLLABORATORS
                      </h2>
                      <br />
                      {fields.map(({ key, name, ...restField }) => {
                        return (
                          <Fragment key={key}>
                            <Row>
                              <Col md={{ offset: 5 }} lg={{ offset: 4 }}>
                                <Form.Item
                                  {...restField}
                                  label={creationMap["_researchers_id"]}
                                  name={[name, "_researchers_id"]}
                                  rules={getRules("_researchers_id")}
                                  extra={getExtra(
                                    "_researchers_id",
                                    inputLengths
                                  )}
                                  style={{ marginBottom: 8 }}
                                >
                                  <Select
                                    className="sub-heading-font"
                                    onSearch={(value) =>
                                      value &&
                                      getFuzzySearchResearchersID_debounced(
                                        value
                                      )
                                    }
                                    placeholder={
                                      creationPlaceholders["_researchers_id"]
                                    }
                                    style={{ width: 300 }}
                                    showSearch
                                    filterOption={false}
                                    notFoundContent={null}
                                    showArrow={false}
                                    loading={status === "loading"}
                                    disabled={key === 0}
                                    labelInValue
                                  >
                                    {results?.map((researcher) => {
                                      return (
                                        <Option
                                          className="sub-heading-font"
                                          key={researcher.id}
                                          value={researcher.id}
                                        >
                                          {getLabelForFuzzySearchResearcherID(
                                            researcher
                                          )}
                                        </Option>
                                      );
                                    })}
                                  </Select>
                                </Form.Item>

                                <Form.Item
                                  {...restField}
                                  label={creationMap["_researchers_roles"]}
                                  name={[name, "_researchers_roles"]}
                                  rules={getRules("_researchers_roles")}
                                  extra={getExtra(
                                    "_researchers_roles",
                                    inputLengths
                                  )}
                                >
                                  <Select
                                    className="sub-heading-font"
                                    mode="tags"
                                    style={{ width: 300 }}
                                    placeholder={
                                      creationPlaceholders["_researchers_roles"]
                                    }
                                  >
                                    {creationRoles.map((role) => (
                                      <Option
                                        className="sub-heading-font"
                                        key={role}
                                        value={role}
                                      >
                                        {role}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>

                              {key !== 0 && (
                                <Col md={{ offset: 1 }} lg={{ offset: 3 }}>
                                  <Button onClick={() => remove(name)} danger>
                                    REMOVE
                                  </Button>
                                </Col>
                              )}
                            </Row>
                          </Fragment>
                        );
                      })}
                      <Row justify="center">
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                        >
                          ADD AN SFU FACULTY COLLABORATOR
                        </Button>
                      </Row>
                    </>
                  );
                }}
              </Form.List>
            </Fragment>
          );

        if (
          creationFieldLabel === "Description" ||
          creationFieldLabel === "Additional Credits"
        )
          return (
            <Fragment key={field}>
              <Divider style={{ width: "100%" }} />
              <Form.Item
                label={creationFieldLabel}
                name={field}
                rules={getRules(field)}
                extra={getExtra(field, inputLengths)}
              >
                <TextArea rows={8} placeholder={creationPlaceholders[field]} />
              </Form.Item>
            </Fragment>
          );

        if (creationFieldLabel === "Creator")
          return (
            <Fragment key={field}>
              <Divider style={{ width: "100%" }} />
              <h2 style={{ textAlign: "center" }}>CREDITS</h2>
              <br />
              <Form.Item
                key={field}
                label={creationFieldLabel}
                name={field}
                rules={getRules(field)}
                extra={getExtra(field, inputLengths)}
              >
                <TextArea rows={1} placeholder={creationPlaceholders[field]} />
              </Form.Item>
            </Fragment>
          );

        if (creationFieldLabel === "Duration")
          return (
            <Form.Item
              key={field}
              label={creationFieldLabel}
              name={undefined}
              rules={getRules(field)}
              extra={getExtra(field, inputLengths)}
            >
              <Form.Item
                name="_duration_minutes"
                style={{ display: "inline-block" }}
              >
                <InputNumber min={0} step={1} placeholder="Minutes" />
              </Form.Item>
              <span style={{ lineHeight: "32px" }}>
                &nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;
              </span>
              <Form.Item
                name="_duration_seconds"
                style={{ display: "inline-block" }}
              >
                <InputNumber min={0} step={1} placeholder="Seconds" />
              </Form.Item>
              <span>&nbsp;&nbsp;&nbsp;Minutes</span>
            </Form.Item>
          );

        return (
          <Form.Item
            key={field}
            label={creationFieldLabel}
            name={field}
            rules={getRules(field)}
            extra={getExtra(field, inputLengths)}
          >
            {creationFieldLabel === "Year of Presentation" ? (
              <DatePicker picker="year" />
            ) : (
              <TextArea
                rows={
                  INPUT_MAX_LENGTH[field as CreationInputLength] < 200 ? 1 : 2
                }
                placeholder={creationPlaceholders[field]}
              />
            )}
          </Form.Item>
        );
      })}
    </>
  );
}
